import React, { useState } from "react";
import CTAButton from "./CTAButton";
import { getCtaVariant } from "../../lib/ab";
import FormConsent from "./FormConsent";

const MENU_SIZES = [
  { value: "under-15", label: "Under 15 dishes" },
  { value: "15-30", label: "15 – 30 dishes" },
  { value: "31-60", label: "31 – 60 dishes" },
  { value: "61-100", label: "61 – 100 dishes" },
  { value: "100-plus", label: "100+ dishes" },
];

const CUISINES = [
  "Indian", "Italian", "Asian / SE Asian", "Café / Brunch",
  "Burgers / American", "Mediterranean", "Mexican / LatAm", "Fine Dining",
  "Bakery / Pastry", "Other",
];

const CHANNELS = [
  "Uber Eats", "DoorDash", "Menulog", "Google Business",
  "Website", "Instagram / Social", "Print / Menu", "OOH / Outdoor",
];

const TIMING = [
  { value: "asap", label: "ASAP · This week" },
  { value: "2-4-weeks", label: "Next 2–4 weeks" },
  { value: "next-month", label: "Next month" },
  { value: "exploring", label: "Just exploring" },
];

export const QualifyingForm = ({ source = "get-a-quote" }) => {
  const [form, setForm] = useState({
    name: "", restaurant: "", email: "", phone: "", suburb: "",
    menuSize: "", cuisine: "", channels: [], timing: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleChannel = (c) =>
    setForm((s) => ({
      ...s,
      channels: s.channels.includes(c)
        ? s.channels.filter((x) => x !== c)
        : [...s.channels, c],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    // Map menuSize to numeric dishes estimate for backend continuity
    const dishesMap = { "under-15": 10, "15-30": 22, "31-60": 45, "61-100": 80, "100-plus": 120 };

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const variant = getCtaVariant();
      const res = await fetch(`${API}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          restaurant: form.restaurant,
          email: form.email,
          phone: form.phone,
          suburb: form.suburb,
          dishes: dishesMap[form.menuSize] || null,
          cuisine: form.cuisine || null,
          channels: form.channels.length ? form.channels : null,
          timing: form.timing || null,
          message: form.message,
          source,
          cta_variant: variant.id,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      console.error("Qualifying form submit failed", err);
      setError("Something went wrong sending the enquiry. Please call 0494 756 111 directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="qualifying-form-success" className="p-10 lg:p-14 border border-ink/15 text-ink">
        <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">Quote request received</p>
        <h3 className="font-display text-3xl lg:text-4xl tracking-editorial leading-tight mb-4">
          Thanks {form.name?.split(" ")[0] || ""} — we&apos;ll be in touch within one business day.
        </h3>
        <p className="text-sm leading-relaxed text-char-mute max-w-md">
          We&apos;ll review your brief, send a written quote, and call to confirm
          the shoot date. If your launch is sooner, mention it in your reply
          and we&apos;ll prioritise availability.
        </p>
      </div>
    );
  }

  const labelCls = "text-xs uppercase tracking-wider2 font-bold text-char/70 block mb-2";
  const inputCls = "w-full bg-transparent border-b border-ink/15 focus:border-ember text-ink placeholder:text-char-mute/60 font-display text-2xl tracking-editorial pb-3 focus:outline-none transition-colors";
  const chipBase = "px-4 py-2 text-[11px] uppercase tracking-wider2 border transition-all duration-300 font-semibold";
  const chipOff = "border-ink/15 text-char hover:border-ink hover:text-ink";
  const chipOn = "border-ember bg-ember text-bone";

  return (
    <form onSubmit={onSubmit} data-testid="qualifying-form" className="text-ink">
      {/* Block 1 — Identity */}
      <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-6">[ 01 ] Who you are</p>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
        <label className="block">
          <span className={labelCls}>Your name *</span>
          <input data-testid="qf-name" required type="text" placeholder="Full name"
            value={form.name} onChange={(e) => setField("name", e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Venue / Brand *</span>
          <input data-testid="qf-restaurant" required type="text" placeholder="e.g. Lune Croissanterie"
            value={form.restaurant} onChange={(e) => setField("restaurant", e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Email *</span>
          <input data-testid="qf-email" required type="email" placeholder="you@venue.com.au"
            value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className={labelCls}>Phone *</span>
          <input data-testid="qf-phone" required type="tel" placeholder="0412 345 678"
            value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls} />
        </label>
        <label className="block sm:col-span-2">
          <span className={labelCls}>Melbourne suburb</span>
          <input data-testid="qf-suburb" type="text" placeholder="e.g. Footscray, Glen Waverley, CBD"
            value={form.suburb} onChange={(e) => setField("suburb", e.target.value)} className={inputCls} />
        </label>
      </div>

      {/* Block 2 — Menu */}
      <div className="mt-16">
        <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-6">[ 02 ] Your menu</p>
        <span className={labelCls}>Menu size</span>
        <div className="flex flex-wrap gap-2">
          {MENU_SIZES.map((m) => (
            <button type="button" key={m.value} data-testid={`qf-size-${m.value}`}
              onClick={() => setField("menuSize", m.value)}
              className={`${chipBase} ${form.menuSize === m.value ? chipOn : chipOff}`}>
              {m.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          <span className={labelCls}>Cuisine</span>
          <div className="flex flex-wrap gap-2">
            {CUISINES.map((c) => (
              <button type="button" key={c} data-testid={`qf-cuisine-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                onClick={() => setField("cuisine", c)}
                className={`${chipBase} ${form.cuisine === c ? chipOn : chipOff}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Block 3 — Channels */}
      <div className="mt-16">
        <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-6">[ 03 ] Where photos will go</p>
        <span className={labelCls}>Channels (tick all that apply)</span>
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map((c) => (
            <button type="button" key={c} data-testid={`qf-channel-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              onClick={() => toggleChannel(c)}
              className={`${chipBase} ${form.channels.includes(c) ? chipOn : chipOff}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Block 4 — Timing */}
      <div className="mt-16">
        <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-6">[ 04 ] When you need it</p>
        <span className={labelCls}>Timing</span>
        <div className="flex flex-wrap gap-2">
          {TIMING.map((t) => (
            <button type="button" key={t.value} data-testid={`qf-timing-${t.value}`}
              onClick={() => setField("timing", t.value)}
              className={`${chipBase} ${form.timing === t.value ? chipOn : chipOff}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Block 5 — Brief */}
      <label className="block mt-16">
        <p className="text-[10px] uppercase tracking-wider2 text-ember font-bold mb-6">[ 05 ] Anything else</p>
        <span className={labelCls}>Tell us about your shoot</span>
        <textarea data-testid="qf-message" rows={4} placeholder="Hero dishes you want shot, brand context, deadlines, anything else worth knowing."
          value={form.message} onChange={(e) => setField("message", e.target.value)}
          className="w-full bg-transparent border-b border-ink/15 focus:border-ember text-ink font-sans text-base leading-relaxed pb-3 focus:outline-none transition-colors resize-none" />
      </label>

      {error && (
        <p data-testid="qf-error" className="mt-6 text-xs text-ember font-bold uppercase tracking-wider2">
          {error}
        </p>
      )}

      <div className="mt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <p className="text-xs text-char-mute max-w-md leading-relaxed">
          No spam. Quotes are written by hand, usually within one business day.
          The more context you share, the tighter we can scope the brief.
        </p>
        <CTAButton type="submit" variant="primary" size="md" disabled={submitting}>
          {submitting ? "Sending…" : "Request My Quote"}
        </CTAButton>
      </div>

      <FormConsent className="mt-6" />
    </form>
  );
};

export default QualifyingForm;
